import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Upload, ImageIcon, Check, UploadCloud, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap } from '@/lib/api';
import { aiDescribeImage } from '@/lib/ai';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';

interface MediaAsset {
  id: string;
  url: string;
  original_name: string;
  mime_type: string;
  size: number;
  width: number | null;
  height: number | null;
  alt: string | null;
}

interface Paginated<T> {
  data: T[];
  total: number;
}

interface MediaPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (url: string, asset?: MediaAsset) => void;
  accept?: 'image' | 'video' | 'application' | 'all';
  title?: string;
}

export function MediaPicker({
  open, onClose, onSelect, accept = 'image', title = 'Seleccionar imagen',
}: MediaPickerProps) {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['media', q, accept],
    queryFn: () => {
      const params = new URLSearchParams({ per_page: '60' });
      if (q) params.set('q', q);
      if (accept !== 'all') params.set('type', accept);
      return unwrap<Paginated<MediaAsset>>(api.get(`/media?${params}`));
    },
    enabled: open,
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post('/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    onSuccess: () => {
      toast.success('Archivo subido');
      void qc.invalidateQueries({ queryKey: ['media'] });
    },
    onError: () => toast.error('Error al subir'),
  });

  const updateAsset = useMutation({
    mutationFn: async ({ id, alt }: { id: string; alt: string }) => {
      return unwrap(api.patch(`/media/${id}`, { alt }));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['media'] });
    },
  });

  const [generatingAlt, setGeneratingAlt] = useState(false);
  const [draftAlt, setDraftAlt] = useState<string>('');

  async function handleGenerateAlt(url: string, id: string) {
    setGeneratingAlt(true);
    try {
      const result = await aiDescribeImage(url);
      const text = result.text || '';
      setDraftAlt(text);
      await updateAsset.mutateAsync({ id, alt: text });
      toast.success('Alt generado');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Error generando alt';
      toast.error(msg);
    } finally {
      setGeneratingAlt(false);
    }
  }

  function uploadFiles(files: FileList | File[]): number {
    const arr = Array.from(files);
    const matching = arr.filter((f) => {
      if (accept === 'all') return true;
      return f.type.startsWith(`${accept}/`);
    });
    if (matching.length === 0 && arr.length > 0) {
      toast.error(`Tipo no soportado. Solo ${accept === 'all' ? 'archivos' : accept + 's'}.`);
      return 0;
    }
    matching.forEach((f) => upload.mutate(f));
    return matching.length;
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (e.dataTransfer.files?.length) {
      uploadFiles(e.dataTransfer.files);
    }
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (!dragging) setDragging(true);
  }

  function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    // Only clear if we leave the dropzone entirely (relatedTarget is outside)
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragging(false);
    }
  }

  const pickedAsset = data?.data.find((a) => a.id === pickedId);

  // Sync draft alt with selection so user can edit the saved value directly.
  useEffect(() => {
    setDraftAlt(pickedAsset?.alt ?? '');
  }, [pickedAsset?.id, pickedAsset?.alt]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Selecciona un archivo o sube uno nuevo.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o alt…" className="pl-9" />
          </div>
          <Button variant="outline" onClick={() => fileInput.current?.click()} disabled={upload.isPending}>
            <Upload className="size-4" /> Subir
          </Button>
          <input
            ref={fileInput}
            type="file"
            className="hidden"
            multiple
            accept={accept === 'image' ? 'image/*' : accept === 'video' ? 'video/*' : '*'}
            onChange={(e) => {
              if (e.target.files?.length) uploadFiles(e.target.files);
              if (fileInput.current) fileInput.current.value = '';
            }}
          />
        </div>

        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          className={cn(
            'relative max-h-[55vh] overflow-y-auto rounded-lg border p-3 transition-colors',
            dragging
              ? 'border-neutral-900 bg-neutral-50'
              : 'border-neutral-200',
          )}
        >
          {dragging && (
            <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center rounded-lg bg-neutral-900/85 text-white">
              <div className="flex flex-col items-center gap-2">
                <UploadCloud className="size-10" />
                <p className="text-sm font-medium">Suelta las imágenes para subirlas</p>
              </div>
            </div>
          )}
          {upload.isPending && (
            <div className="mb-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Subiendo archivos…
            </div>
          )}
          {isLoading && <p className="text-sm text-neutral-500">Cargando…</p>}
          {!isLoading && (data?.data.length ?? 0) === 0 && !dragging && (
            <p className="py-8 text-center text-sm text-neutral-400">
              No hay archivos. Arrastra una imagen aquí o pulsa “Subir”.
            </p>
          )}
          <div className="grid grid-cols-3 gap-2 md:grid-cols-5 lg:grid-cols-6">
            {data?.data.map((m) => {
              const isImage = m.mime_type.startsWith('image/');
              const picked = pickedId === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setPickedId(m.id)}
                  onDoubleClick={() => {
                    onSelect(m.url, m);
                    onClose();
                  }}
                  className={cn(
                    'group relative aspect-square overflow-hidden rounded-md border bg-white transition-all',
                    picked ? 'border-neutral-900 ring-2 ring-neutral-900/20' : 'border-neutral-200 hover:border-neutral-400',
                  )}
                >
                  {isImage ? (
                    <img src={m.url} alt={m.alt ?? m.original_name} className="h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <div className="grid h-full w-full place-items-center bg-neutral-100">
                      <ImageIcon className="size-6 text-neutral-400" />
                    </div>
                  )}
                  {picked && (
                    <span className="absolute right-1 top-1 grid size-5 place-items-center rounded-full bg-neutral-900 text-white">
                      <Check className="size-3" />
                    </span>
                  )}
                  <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/80 to-transparent px-1.5 py-0.5 text-left text-[10px] text-white opacity-0 group-hover:opacity-100">
                    {m.original_name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {pickedAsset && pickedAsset.mime_type.startsWith('image/') && (
          <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3">
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <label className="text-xs font-medium text-neutral-700">
                Texto alternativo (accesibilidad / SEO)
              </label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void handleGenerateAlt(pickedAsset.url, pickedAsset.id)}
                disabled={generatingAlt}
                className="gap-1.5 text-violet-700 hover:bg-violet-50"
              >
                {generatingAlt ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Sparkles className="size-3.5" />
                )}
                Generar alt con AI
              </Button>
            </div>
            <div className="flex gap-2">
              <Input
                value={draftAlt}
                onChange={(e) => setDraftAlt(e.target.value)}
                placeholder="Describe la imagen para lectores de pantalla…"
                maxLength={200}
              />
              <Button
                variant="outline"
                size="sm"
                disabled={updateAsset.isPending || draftAlt === (pickedAsset.alt ?? '')}
                onClick={() => updateAsset.mutate({ id: pickedAsset.id, alt: draftAlt })}
              >
                Guardar
              </Button>
            </div>
            <p className="mt-1 text-[10px] text-neutral-500">
              {draftAlt.length}/200 caracteres
            </p>
          </div>
        )}

        <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
          <div className="text-xs text-neutral-500">
            {pickedAsset
              ? `${pickedAsset.original_name} · ${pickedAsset.width && pickedAsset.height ? `${pickedAsset.width}×${pickedAsset.height}` : '—'}`
              : 'Selecciona un archivo'}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button
              disabled={!pickedAsset}
              onClick={() => {
                if (pickedAsset) {
                  onSelect(pickedAsset.url, pickedAsset);
                  onClose();
                }
              }}
            >
              Seleccionar
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
